import type { ReactNode } from 'react';
import { GuideHeader } from './GuideHeader';

/** The two processing modes, Manual first, as Job setup › Review thresholds › Pilot mode names them. */
const modes = [
  {
    name: 'Manual',
    value: 'human-review',
    then: 'Waits in Paige’s review for a person, with its state review; once they confirm it, it is exported and delivered like any other. A new job starts here.',
  },
  {
    name: 'Autopilot',
    value: 'ai-review',
    then: 'Goes to Cortexa’s AI reviewer, which checks it against the page, corrects and confirms what it can, and releases it; what it cannot settle is exported with its reasons (exportedWithExceptions). A document with no type gets one from the reviewer. Only a document the reviewer could give no type, or Cortexa read nothing from, waits for a person.',
  },
] as const;

/** How a job reads, and the processing mode that decides what waits for a person. */
export function ProcessingModesGuide(): ReactNode {
  return (
    <article className="docs-page">
      <GuideHeader slug="processing-modes" />
      <HowAJobReads />
      <Modes />
      <WhatYouSee />
    </article>
  );
}

function HowAJobReads(): ReactNode {
  return (
    <section className="docs-section" aria-labelledby="reads-title">
      <h2 id="reads-title">How a job reads</h2>
      <p>
        <strong>A job with no setup</strong>, such as one you have just made through the API, reads
        nothing yet. A batch sent to it is kept, unread and uncharged: it stays{' '}
        <code>processing</code> and its <code>waitingFor</code> has the code <code>job-setup</code>,
        with <code>documents</code> 0 and <code>pages</code> saying how much waits. Your company’s
        admins are told in Paige and by email. No processing mode releases it: the batch is read
        once a person saves the job’s setup, and nothing has to be sent again.
      </p>
      <p>
        <strong>A job set up in Paige</strong> is read with what it has learned: its own document
        types, each with its own fields and tables, the layouts and corrections it has seen, and its
        rules. Open the job in Paige and choose Set up from these documents, or give it files of
        your own: its setup reads a few of them and proposes the types and fields. Once the setup is
        saved, what was waiting and every document sent afterwards, through the API as well, is read
        that way, with the job’s field names and the table rows in <code>lineItems</code>. A batch
        the job was set up from answers <code>supersededBy</code>, the batch its files are read
        under.
      </p>
    </section>
  );
}

function Modes(): ReactNode {
  return (
    <section className="docs-section" aria-labelledby="modes-title">
      <h2 id="modes-title">What waits for a person: the processing mode</h2>
      <p>
        Every reading ends in a check. A document that passes is confirmed and exported at once. A
        document Cortexa is unsure of (a missing value, a low confidence, an uncertain type) is
        finished according to the job’s processing mode: <code>processingMode</code> on{' '}
        <code>POST /jobs</code> and <code>PATCH /jobs/{'{jobId}'}</code>, and the same setting as
        Pilot mode in Paige under the job’s setup, Review thresholds. Changing it to{' '}
        <code>ai-review</code> also applies to the documents the job already holds.{' '}
        <code>ai-review</code> answers 409 <code>ai_review_unavailable</code> where Cortexa’s AI
        reviewer is not available to your company. <code>full-auto</code> is deprecated, an alias of{' '}
        <code>ai-review</code>, and saved and answered as it.
      </p>
      <table className="docs-table">
        <thead>
          <tr>
            <th scope="col">Mode</th>
            <th scope="col">
              <code>processingMode</code>
            </th>
            <th scope="col">A document Cortexa is unsure of</th>
          </tr>
        </thead>
        <tbody>
          {modes.map((m) => (
            <tr key={m.name}>
              <th scope="row">{m.name}</th>
              <td>
                <code>{m.value}</code>
              </td>
              <td>{m.then}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function WhatYouSee(): ReactNode {
  return (
    <section className="docs-section" aria-labelledby="see-title">
      <h2 id="see-title">What the API shows</h2>
      <ul className="docs-list">
        <li>
          A document’s <code>state</code>: <code>processing</code>, then <code>exported</code> when
          it passed or was released, or <code>review</code> while it waits for a person. Then its{' '}
          <code>waitingFor</code> says why, for a program: <code>reasons</code>, each with a stable{' '}
          <code>code</code> (<code>field-confidence</code>, <code>type-confidence</code>,{' '}
          <code>missing-required</code>, <code>no-values</code> and the rest, listed in the
          reference), a plain <code>message</code> and the <code>action</code> that ends the wait.{' '}
          <code>exception</code> means it stopped (a file that cannot be read, a destination that
          refused it); its <code>reason</code> says why, and{' '}
          <code>GET /jobs/{'{jobId}'}/exceptions</code> lists them.
        </li>
        <li>
          A batch settles as <code>complete</code> when every document is exported,{' '}
          <code>review</code> when the rest are exported and some wait for a person, or{' '}
          <code>needs_attention</code>. A batch in <code>review</code> carries the same{' '}
          <code>waitingFor</code>, counted over its documents; it stays in <code>review</code> until
          they are confirmed or released, so do not poll it for <code>complete</code>. A document
          confirmed later is delivered then: a webhook destination is the way to hear about it
          without polling. Your company’s people are told too: one notice a job in Paige, and an
          email to the admins, when documents wait for review and nobody is at its review screen.
        </li>
        <li>
          While the company has no pages, documents wait (nothing is read past zero) and carry on in
          order when pages arrive. A paused job keeps what arrives and reads nothing until it is
          resumed (<code>POST /jobs/{'{jobId}'}/state</code>).
        </li>
      </ul>
    </section>
  );
}
